/**
 * V2 Carousel Style Configuration
 *
 * Factory for style section configs adapted to V2 carousel schema paths.
 * V2 uses .appearance instead of .styles, .layout.insets instead of .box.styles.
 *
 * Reuses shared V2 text/image style field factories from v2OptionsListStyleConfig.
 *
 * Sprint: schema-v2-migration, Sprint 4 fix
 */

import type { ListStyleSectionConfig } from '../types/listSection.types';
import {
  createV2TextStyleFields,
  createV2ImageStyleFields,
} from './v2OptionsListStyleConfig';

/**
 * Create V2 carousel style sections
 *
 * Returns 4 collapsible sections matching V1 SliderStyleSections:
 * 1. Title styles (fontSize, fontWeight, color, align + padding)
 * 2. Subtitle styles
 * 3. Subtitle 2 styles
 * 4. Image styles (dimensions, scaleMode, cornerRadius + padding)
 *
 * Visibility conditions based on carousel kind:
 * - Image: no text sections
 * - ImageTitle: title only
 * - ImageTitleSubtitle1: title + subtitle
 * - ImageTitleSubtitle1Subtitle2: all
 */
export function createV2CarouselStyleSections(): ListStyleSectionConfig[] {
  const carouselBase = 'content.carousel';

  return [
    // Title styles
    {
      id: 'titleStyles',
      title: 'Title styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${carouselBase}.items.0.content.title`),
      paddingConfig: {
        basePath: `${carouselBase}.items.0.content.title.layout.insets`,
        label: 'Title padding',
      },
      visibilityCondition: (kind: string) => kind !== 'visual',
    },
    // Subtitle styles
    {
      id: 'subtitleStyles',
      title: 'Subtitle styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${carouselBase}.items.0.content.subtitle`),
      paddingConfig: {
        basePath: `${carouselBase}.items.0.content.subtitle.layout.insets`,
        label: 'Subtitle padding',
      },
      visibilityCondition: (kind: string) => kind === 'visualCaptionDetail' || kind === 'visualCaptionDetailExtra',
    },
    // Detail styles
    {
      id: 'detailStyles',
      title: 'Detail styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${carouselBase}.items.0.content.detail`),
      paddingConfig: {
        basePath: `${carouselBase}.items.0.content.detail.layout.insets`,
        label: 'Detail padding',
      },
      visibilityCondition: (kind: string) => kind === 'visualCaptionDetailExtra',
    },
    // Image styles
    {
      id: 'imageStyles',
      title: 'Image styles',
      defaultExpanded: false,
      fields: createV2ImageStyleFields(`${carouselBase}.items.0.content.image`),
      paddingConfig: {
        basePath: `${carouselBase}.items.0.content.image.layout.insets`,
        label: 'Image padding',
      },
    },
  ];
}
